/**
 * Backward-compatible Clarifi export API.
 *
 * Clarifi now uses the same permission-gated, organization-scoped, audited
 * server pipeline as the Loan Center data-export workspace. Existing callers
 * can continue using exportLoanToClarifi/exportAndDownloadClarifi unchanged.
 */

import { createComponentLogger } from '@/utils/logger';
import type { ClarifiExportResult } from '@/types/clarifi-export';
import {
  generateLoanDataExport,
  preflightLoanDataExport,
  resolveLoanDataExportNumbers
} from '@/services/loanDataExportService';

const log = createComponentLogger('ClarifiExportService', 'encompass');

export async function exportLoanToClarifi(
  loanNumber: string
): Promise<ClarifiExportResult> {
  const timer = log.startTimer('clarifi_export');
  try {
    const normalizedLoanNumber = loanNumber.trim();
    const matches = await resolveLoanDataExportNumbers([normalizedLoanNumber]);
    const loan = matches.find(
      (candidate) => candidate.loanNumber.toLowerCase() === normalizedLoanNumber.toLowerCase()
    );
    if (!loan) {
      return {
        success: false,
        filename: '',
        error: `Loan ${normalizedLoanNumber} was not found or is not eligible for export`,
        rowCount: 0
      };
    }

    const validation = await preflightLoanDataExport([loan.id], 'clarifi-v1');
    if (validation.warningCount > 0) {
      return {
        success: false,
        filename: '',
        error: 'This loan has missing Clarifi fields. Open Data Exports to review and acknowledge the warnings.',
        rowCount: 0
      };
    }

    const result = await generateLoanDataExport({
      loanIds: [loan.id],
      templateId: 'clarifi-v1',
      requestId: crypto.randomUUID(),
      acknowledgeWarnings: false
    });
    const fileName = result.export.file_name || 'Clarifi_DataTape.xlsx';
    const response = await fetch(result.download.signedUrl);
    if (!response.ok) {
      throw new Error('The Clarifi workbook could not be retrieved');
    }
    const blob = await response.blob();
    log.endTimer(timer, { loanNumber: normalizedLoanNumber, success: true });
    return {
      success: true,
      filename: fileName,
      blob,
      rowCount: 1
    };
  } catch (error) {
    log.error('Clarifi export failed', { loanNumber }, error as Error);
    log.endTimer(timer, { loanNumber, success: false });
    return {
      success: false,
      filename: '',
      error: error instanceof Error ? error.message : 'Export failed',
      rowCount: 0
    };
  }
}

export function downloadClarifiExport(result: ClarifiExportResult): void {
  if (!result.success || !result.blob) {
    log.warn('Cannot download failed export', { filename: result.filename });
    return;
  }

  const url = URL.createObjectURL(result.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = result.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function exportAndDownloadClarifi(
  loanNumber: string
): Promise<ClarifiExportResult> {
  const result = await exportLoanToClarifi(loanNumber);
  if (result.success && result.blob) {
    downloadClarifiExport(result);
  }
  return result;
}
